import React, { useEffect, useState } from "react";
import LimaHeader from "../components/LimaHeader";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Building2, FileText, Trash2, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  pendente_analise: { label: "Aguardando análise", cls: "bg-amber-100 text-amber-800" },
  em_analise:       { label: "Em análise",         cls: "bg-blue-100 text-blue-800" },
  concluido:        { label: "Análise concluída",  cls: "bg-emerald-100 text-emerald-800" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [companies, setCompanies] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cnpj: "", razao_social: "", nome_fantasia: "", endereco: "",
    empregados: 0, cnae_principal: "", responsavel: "", cargo: "",
  });
  const nav = useNavigate();

  const load = async () => {
    const [c, d] = await Promise.all([api.get("/companies"), api.get("/diagnostics")]);
    setCompanies(c.data);
    setDiagnostics(d.data);
  };

  useEffect(() => { load(); }, []);

  const createCompany = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/companies", { ...form, empregados: Number(form.empregados) });
      toast.success("Empresa cadastrada");
      setOpen(false);
      setCompanies([data, ...companies]);
      nav(`/diagnostico/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar");
    }
  };

  const removeCompany = async (id) => {
    if (!confirm("Remover empresa e todos os diagnósticos?")) return;
    await api.delete(`/companies/${id}`);
    toast.success("Removida");
    load();
  };

  const diagsFor = (cid) => diagnostics.filter(d => d.company_id === cid);

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <LimaHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">PAINEL EXECUTIVO</span>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#10243f] mt-1">
              Empresas & Diagnósticos
            </h1>
            <p className="text-slate-600 mt-2">Gerencie múltiplas empresas e acompanhe o histórico de exposições.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#c7a45a] hover:bg-[#b58f43] text-[#10243f] font-bold h-11 px-5" data-testid="new-company-btn">
                <Plus size={18} className="mr-2" /> Nova empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#10243f]">Cadastrar empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={createCompany} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Razão social *</Label>
                  <Input required data-testid="cmp-razao" value={form.razao_social}
                    onChange={e => setForm({ ...form, razao_social: e.target.value })} />
                </div>
                <div><Label>CNPJ *</Label>
                  <Input required data-testid="cmp-cnpj" value={form.cnpj}
                    onChange={e => setForm({ ...form, cnpj: e.target.value })} />
                </div>
                <div><Label>Nome fantasia</Label>
                  <Input data-testid="cmp-fantasia" value={form.nome_fantasia}
                    onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} />
                </div>
                <div><Label>Nº de empregados</Label>
                  <Input type="number" min="0" data-testid="cmp-empregados" value={form.empregados}
                    onChange={e => setForm({ ...form, empregados: e.target.value })} />
                </div>
                <div><Label>CNAE principal</Label>
                  <Input data-testid="cmp-cnae" placeholder="33.21-0-00" value={form.cnae_principal}
                    onChange={e => setForm({ ...form, cnae_principal: e.target.value })} />
                </div>
                <div className="col-span-2"><Label>Endereço</Label>
                  <Input data-testid="cmp-endereco" value={form.endereco}
                    onChange={e => setForm({ ...form, endereco: e.target.value })} />
                </div>
                <div><Label>Responsável</Label>
                  <Input data-testid="cmp-resp" value={form.responsavel}
                    onChange={e => setForm({ ...form, responsavel: e.target.value })} />
                </div>
                <div><Label>Cargo</Label>
                  <Input data-testid="cmp-cargo" value={form.cargo}
                    onChange={e => setForm({ ...form, cargo: e.target.value })} />
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="submit" className="bg-[#10243f] hover:bg-[#19385e] text-white" data-testid="cmp-save">
                    Salvar e iniciar diagnóstico
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {companies.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center">
            <Building2 size={40} className="mx-auto text-slate-400 mb-3" />
            <h3 className="font-display font-bold text-xl text-[#10243f]">Nenhuma empresa cadastrada</h3>
            <p className="text-slate-500 mt-2">Cadastre a primeira empresa para iniciar um diagnóstico SST.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {companies.map(c => {
              const ds = diagsFor(c.id);
              const last = ds[0];
              return (
                <div key={c.id} data-testid={`company-card-${c.id}`}
                     className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#10243f]/30 hover:-translate-y-1 transition-transform">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <h3 className="font-display font-bold text-[#10243f] truncate">{c.razao_social}</h3>
                      <p className="text-xs text-slate-500 mt-1">{c.cnpj} · {c.empregados} empregados</p>
                    </div>
                    <button onClick={() => removeCompany(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-${c.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {last ? (
                    <div className="border-t border-slate-100 pt-4 mt-4">
                      {isAdmin && last.result ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] tracking-widest uppercase text-slate-500">Último diagnóstico</span>
                            <span className={`text-xs font-bold ${
                              last.result.color === "red" ? "text-red-700" :
                              last.result.color === "orange" ? "text-orange-700" : "text-emerald-700"
                            }`}>{last.result.level}</span>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-display font-black text-3xl text-[#10243f]">{last.result.score}%</span>
                            <span className="text-xs text-slate-500">score · multa {last.result.fine_band}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-amber-600" />
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABEL[last.status || "pendente_analise"].cls}`}>
                            {STATUS_LABEL[last.status || "pendente_analise"].label}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-2">
                        {ds.length} diagnóstico(s) · último em {new Date(last.created_at).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 border-t border-slate-100 pt-4 mt-4">Ainda sem diagnóstico.</p>
                  )}
                  <div className="flex gap-2 mt-5">
                    <Button size="sm" className="bg-[#10243f] hover:bg-[#19385e] text-white flex-1"
                            onClick={() => nav(`/diagnostico/${c.id}`)} data-testid={`start-diag-${c.id}`}>
                      Novo diagnóstico <ChevronRight size={14} className="ml-1" />
                    </Button>
                    {last && isAdmin && (
                      <Button size="sm" variant="outline" className="border-slate-300"
                              onClick={() => nav(`/relatorio/${last.id}`)} data-testid={`view-report-${c.id}`}>
                        <FileText size={14} className="mr-1" /> Relatório
                      </Button>
                    )}
                    {last && !isAdmin && (
                      <Button size="sm" variant="outline" className="border-slate-300"
                              onClick={() => nav(`/concluido/${last.id}`)} data-testid={`view-status-${c.id}`}>
                        Status
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
